import { useCallback, useState } from 'react';

/**
 * Per-team custom fields — a no-code form extension. Definitions are JSONB-style
 * config (stored in localStorage now → a per-tenant `team_fields` table, RLS-
 * scoped, in production). Values live on each ticket's `custom` JSONB.
 */
export type CustomFieldType = 'text' | 'number' | 'select' | 'date' | 'checkbox';

export type CustomField = {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options?: string[];
  required?: boolean;
};

const LS_KEY = 'compass-team-fields';

function loadAll(): Record<string, CustomField[]> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}

function slug(label: string) {
  return (
    'cf_' +
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  );
}

function newId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `f-${Date.now()}`;
}

/** A team's custom fields plus CRUD. */
export function useTeamFields(teamId: string) {
  const [map, setMap] = useState<Record<string, CustomField[]>>(() => loadAll());

  const persist = useCallback((next: Record<string, CustomField[]>) => {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setMap(next);
  }, []);

  const fields = map[teamId] ?? [];

  const add = useCallback(
    (input: Omit<CustomField, 'id' | 'key'>) => {
      const all = loadAll();
      const field: CustomField = { ...input, id: newId(), key: slug(input.label) };
      persist({ ...all, [teamId]: [...(all[teamId] ?? []), field] });
    },
    [teamId, persist],
  );

  const remove = useCallback(
    (id: string) => {
      const all = loadAll();
      persist({ ...all, [teamId]: (all[teamId] ?? []).filter((f) => f.id !== id) });
    },
    [teamId, persist],
  );

  return { fields, add, remove };
}

/** Read-only helper: a team's fields (used where the hook isn't convenient). */
export function teamFieldsFor(teamId: string): CustomField[] {
  return loadAll()[teamId] ?? [];
}
